import { useEffect } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

export default function ProcessDetails() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const timeline = [
    { month: '0-1', title: 'Valg & Planlegging', items: ['Ta kontakt med oss', 'Få tilbud samme dag', '72 500 kr reservasjonsbeløp ved aksept'] },
    { month: '1-3', title: 'Søknad & Godkjenning', items: ['Vi håndterer hele byggesøknaden', 'Prosjektering av vann, avløp og strøm', 'Fotorealistisk visualisering'] },
    { month: '3-4', title: 'Produksjonstart', items: ['30% betaling starter produksjonen', 'Grunnarbeid kan starte parallelt'] },
    { month: '4-5', title: 'Produksjon i Våle', items: ['6-8 uker produksjon', 'Millimeterpresisjon', 'Kvalitetskontroll'] },
    { month: '5-6', title: 'Transport & Montering', items: ['Transporter fra fabrikk', 'Kranmontering på tomten', 'Sluttbetaling'] },
    { month: '6+', title: 'Nyt din Badstue', items: ['Kun tilkobling til infrastruktur gjenstår', 'Digital dokumentasjon', 'Innflytningsklar'] }
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      <div className="pt-32 pb-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-32"
          >
            <h1 className="font-heading text-6xl md:text-7xl font-light leading-tight mb-8">
              Det skal være Enkelt å Skape Badstuer
            </h1>
            <p className="font-serif text-lg text-foreground/70 leading-relaxed max-w-2xl">
              Hytta er et sted for ro, nærvær og natur. Prosessen kan føles uoversiktlig, men med Lykke gjør vi reisen enkel. Alle ledd fra A til Å er inkludert.
            </p>
          </motion.div>

          {/* Hero Image */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-32 rounded-lg overflow-hidden h-96"
          >
            <img
              src="https://cdn.prod.website-files.com/69675e688a8322c8f309fa2f/699c33d4787c4e0754ecd6d2_toppbanner-prosess-varanger.jpg"
              alt="Prosess"
              className="w-full h-full object-cover"
            />
          </motion.div>

          {/* Timeline */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-32"
          >
            <h2 className="font-heading text-4xl font-light mb-16">Din Reise med LYKKE NOMAD</h2>

            <div className="grid md:grid-cols-2 gap-6">
              {timeline.map((phase, idx) => (
                <div key={idx} className="pt-6 border-t border-border/40">
                  <p className="font-body text-sm tracking-widest uppercase text-[#a6b894] font-semibold mb-3">Måned {phase.month}</p>
                  <h3 className="font-heading text-lg font-light mb-6 text-[#a6b894]">{phase.title}</h3>
                  <ul className="space-y-2">
                    {phase.items.map((item, i) => (
                      <li key={i} className="font-serif text-base text-foreground/70">• {item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="mt-16 pt-12 border-t border-border/40">
              <p className="font-serif text-foreground/70">
                💡 <strong>Tips:</strong> Start minst ett år før ønsket levering for god tid til søknadsprosessen.
              </p>
            </div>
          </motion.section>

          {/* Detailed Steps */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mb-32"
          >
            <h2 className="font-heading text-4xl font-light mb-8">Din Badstue — Steg for Steg</h2>
            <p className="font-serif text-lg text-foreground/70 mb-20">Tydelig, forutsigbar og lagt opp for en sømløs reise fra idé til ferdig resultat.</p>

            <div className="space-y-32">
              {[
                {
                  num: '1',
                  title: 'Bygg din Badstue',
                  desc: 'Du velger hvilken badstue som passer ditt prosjekt. Vi gir deg tilbud samme dag og rask avklaring på videre prosess.',
                  img: 'https://cdn.prod.website-files.com/69675e688a8322c8f309fa2f/698e17f4ad0a2e4c7d45c147_bygg-din-omgi.jpg'
                },
                {
                   num: '1',
                   title: 'Reservasjon & Planlegging',
                  desc: 'Ved aksept faktureres et reservasjonsbeløp på 72 500 kr. Dette dekker tegning, søknad til kommunen, prosjektering og fotorealistisk visualisering.',
                  img: 'https://cdn.prod.website-files.com/69675e688a8322c8f309fa2f/699c3a74d0b45b4da5dba4a8_reservasjon-og-planlegging-bok.jpg'
                },
                {
                  num: '3',
                  title: 'Byggesøknad',
                  desc: 'Vi håndterer hele søknadsprosessen til kommunen. Kartlegging, prosjektering, dialog og innsending av komplett søknad.',
                  img: 'https://cdn.prod.website-files.com/69675e688a8322c8f309fa2f/698e1ef5872fd5ac3ac2cfc2_S%C3%B8lvane%20Gard.jpg'
                },
                {
                  num: '4',
                  title: 'Produksjon',
                  desc: 'Når søknaden er godkjent, starter produksjonen i fabrikken vår. Badstuen bygges ferdig innendørs i løpet av 6-8 uker med millimeterpresisjon.',
                  img: 'https://cdn.prod.website-files.com/69675e688a8322c8f309fa2f/698e1ef12e27cc1af6dbe8c7_Fabrikken.jpg'
                },
                {
                  num: '5',
                  title: 'Infrastruktur & Fundament',
                  desc: 'Mens produksjonen pågår, startes grunnarbeidet på tomten. Vi koordinerer grøfter, stikkledninger, fundament og adkomstvei.',
                  img: 'https://cdn.prod.website-files.com/69675e688a8322c8f309fa2f/698e1e18060b73b1ad3493aa_infrastruktur-og-fundament.jpg'
                },
                {
                  num: '6',
                  title: 'Transport & Montering',
                  desc: 'Enheten transporteres direkte til tomten og løftes presist på plass av kranteamet vårt. Hele montasjen gjennomføres som regel på én dag.',
                  img: 'https://cdn.prod.website-files.com/69675e688a8322c8f309fa2f/698e17d8b69e07c157009e92_transport-og-montering.jpg'
                },
                {
                  num: '7',
                  title: 'Overtakelse & Dokumentasjon',
                  desc: 'Vi gjennomfører overtakelse sammen med deg. Du får FDV-dokumentasjon digitalt, samsvarserklæringer og garantidokumenter.',
                  img: 'https://cdn.prod.website-files.com/69675e688a8322c8f309fa2f/698e1e2ea280bf15f9722ddc_overtakelse-og-dokumentasjon.jpg'
                }
              ].map((step, idx) => (
                <div key={idx}>
                  <div className="grid md:grid-cols-2 gap-16 items-center">
                    {idx % 2 === 0 ? (
                      <>
                        <div>
                          <div className="flex items-baseline gap-4 mb-6 group">
                            <div className="font-heading text-4xl font-light text-foreground/20 group-hover:text-[#a6b894] transition-colors">{step.num}</div>
                            <h3 className="font-heading text-2xl font-light text-foreground/70 group-hover:text-[#a6b894] transition-colors">{step.title}</h3>
                          </div>
                          <p className="font-serif text-foreground/70 leading-relaxed">{step.desc}</p>
                        </div>
                        <div className="rounded-lg overflow-hidden h-72">
                          <img src={step.img} alt={step.title} className="w-full h-full object-cover" />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="rounded-lg overflow-hidden h-72">
                          <img src={step.img} alt={step.title} className="w-full h-full object-cover" />
                        </div>
                        <div>
                          <div className="flex items-baseline gap-4 mb-6 group">
                            <div className="font-heading text-4xl font-light text-foreground/20 group-hover:text-[#a6b894] transition-colors">{step.num}</div>
                            <h3 className="font-heading text-2xl font-light text-foreground/70 group-hover:text-[#a6b894] transition-colors">{step.title}</h3>
                          </div>
                          <p className="font-serif text-foreground/70 leading-relaxed">{step.desc}</p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </motion.section>

          {/* What's Included */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mb-20"
          >
            <h2 className="font-heading text-4xl font-light mb-8">Alt Inkludert — Transparent Prising</h2>
            <p className="font-serif text-lg text-foreground/70 mb-16 leading-relaxed">
              I alle våre priser inngår alt du trenger for å komme fra idé til ferdig resultat. Full forutsigbarhet og ryddig prosess.
            </p>

            <div className="space-y-8">
              <div className="pt-8 border-t border-border/40">
                <h3 className="font-heading text-xl font-light mb-4">Byggesøknad & Arkitekt</h3>
                <ul className="font-serif text-base text-foreground/70 space-y-2">
                  <li>• Komplett søknadsprosess</li>
                  <li>• Arkitektveiledning</li>
                  <li>• Fotorealistisk visualisering</li>
                </ul>
              </div>

              <div className="pt-8 border-t border-border/40">
                <h3 className="font-heading text-xl font-light mb-4">Produksjon & Kvalitet</h3>
                <ul className="font-serif text-base text-foreground/70 space-y-2">
                  <li>• Komplett norsk produksjon</li>
                  <li>• TEK17-godkjent</li>
                  <li>• Materialer av høy kvalitet</li>
                </ul>
              </div>

              <div className="pt-8 border-t border-border/40">
                <h3 className="font-heading text-xl font-light mb-4">Dokumentasjon</h3>
                <ul className="font-serif text-base text-foreground/70 space-y-2">
                  <li>• FDV-dokumentasjon i Boligmappa</li>
                  <li>• Samsvarserklæringer</li>
                  <li>• Garantidokumenter</li>
                </ul>
              </div>

              <div className="pt-8 border-t border-border/40">
                <h3 className="font-heading text-xl font-light mb-4">Prosjektkoordinator</h3>
                <ul className="font-serif text-base text-foreground/70 space-y-2">
                  <li>• Dedikert kontaktperson</li>
                  <li>• Sikrer fremdrift</li>
                  <li>• Koordinering av alle involverte</li>
                </ul>
              </div>
            </div>

            <div className="mt-16 pt-12 border-t border-border/40 text-center">
              <h3 className="font-heading text-2xl font-light mb-4">Fra Idé til Ferdig på 6-12 Måneder</h3>
              <p className="font-serif text-foreground/70 leading-relaxed mb-10 max-w-2xl mx-auto">
                Med Lykke er hele prosessen planlagt, koordinert og dokumentert. Du trenger bare å fokusere på det som betyr noe — å skape et sted man gleder seg til å oppleve igjen og igjen.
              </p>
              <a
                href="/om-oss#kontakt"
                className="inline-block px-8 py-3 border border-foreground text-foreground font-sans tracking-widest text-sm font-semibold rounded-sm hover:bg-foreground hover:text-background transition-colors"
              >
                START PLANLEGGINGEN
              </a>
            </div>
          </motion.section>
        </div>
      </div>

      <Footer />
    </div>
  );
}